import React, { ReactNode, useEffect, useRef } from "react";

type DraggableTableWrapperProps = {
  children: ReactNode;
};

const DraggableTableWrapper: React.FC<DraggableTableWrapperProps> = ({ children }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // These variables must be mutable across events, so use refs
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return () => null;

        const onMouseDown = (e: MouseEvent) => {
            isDown.current = true;
            container.classList.add("active");
            startX.current = e.pageX - container.offsetLeft;
            scrollLeft.current = container.scrollLeft;
        };

        const onMouseLeave = () => {
            isDown.current = false;
            container.classList.remove("active");
        };

        const onMouseUp = () => {
            isDown.current = false;
            container.classList.remove("active");
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isDown.current) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = x - startX.current;
            container.scrollLeft = scrollLeft.current - walk;
        };

        container.addEventListener("mousedown", onMouseDown);
        container.addEventListener("mouseleave", onMouseLeave);
        container.addEventListener("mouseup", onMouseUp);
        container.addEventListener("mousemove", onMouseMove);

        return () => {
            container.removeEventListener("mousedown", onMouseDown);
            container.removeEventListener("mouseleave", onMouseLeave);
            container.removeEventListener("mouseup", onMouseUp);
            container.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                overflowX: "auto",
                cursor: "grab",
                userSelect: "none",
            }}
        >
            {children}
        </div>
    );
};

export default DraggableTableWrapper;
